import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input, Modal, Spin, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { useCurrentAccount } from '../layout/currentAccountContext';
import {
  addCollaborationCreators,
  getCollaborationDetail,
  getInfluencerCollectionPage,
  getInfluencerPage
} from '../../../store/request';
import type { TargetCollaborationCreator } from '../../../types/collaboration';
import type { InfluencerCollectionRecord, InfluencerRecord } from '../../../types/influencer';

type TalentTab = 'mine' | 'square';

type TalentSource = {
  influencerId?: string;
  creatorOpenId?: string;
  nickname?: string;
  username?: string;
  email?: string;
  avatarUrl?: string;
  selectionRegion?: string;
  categoryNames?: string[];
  isBlacklist?: boolean | null;
  status?: number | null;
};

type PickerTalent = {
  id: string;
  name: string;
  avatarUrl: string;
  initial: string;
  meta: string;
};

type AddInviteTalentModalProps = {
  open: boolean;
  shopId?: string | null;
  collaborationIds: number[];
  onClose: () => void;
  onSuccess?: () => void;
};

function isBlocked(row: TalentSource) {
  return Boolean(row.isBlacklist) || row.status === 3;
}

function toPickerTalent(row: TalentSource, hasEmailLabel: string): PickerTalent | null {
  if (isBlocked(row)) return null;
  const id = String(row.influencerId ?? '').trim() || String(row.creatorOpenId ?? '').trim();
  if (!id) return null;
  const name = row.nickname || row.username || id;
  const handle = row.username?.replace(/^@/, '').trim() || '';
  const email = row.email?.trim() || '';
  const categories = row.categoryNames?.filter(Boolean).slice(0, 2).join(' · ') ?? '';
  return {
    id,
    name,
    avatarUrl: row.avatarUrl?.trim() || '',
    initial: name.slice(0, 1).toUpperCase() || '?',
    meta:
      [handle ? `@${handle}` : '', row.selectionRegion, categories, email ? hasEmailLabel : ''].filter(Boolean).join(' · ') ||
      '—'
  };
}

function creatorToSource(creator: TargetCollaborationCreator): TalentSource {
  return {
    influencerId: creator.influencerId,
    creatorOpenId: creator.creatorOpenId,
    nickname: creator.nickname || creator.creatorName || creator.platformNickname || creator.username || undefined,
    username: creator.username || creator.platformUsername || undefined,
    avatarUrl: creator.avatarUrl,
    selectionRegion: creator.selectionRegion || undefined
  };
}

export function AddInviteTalentModal({ open, shopId, collaborationIds, onClose, onSuccess }: AddInviteTalentModalProps) {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const [tab, setTab] = useState<TalentTab>('mine');
  const [keyword, setKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [existingCreators, setExistingCreators] = useState<TargetCollaborationCreator[]>([]);
  const [existingLoading, setExistingLoading] = useState(false);
  const [mineRecords, setMineRecords] = useState<InfluencerCollectionRecord[]>([]);
  const [mineTotal, setMineTotal] = useState(0);
  const [mineLoading, setMineLoading] = useState(false);
  const [squareRecords, setSquareRecords] = useState<InfluencerRecord[]>([]);
  const [squareLoading, setSquareLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const currentShopId = shopId?.trim() || '';
  const inviteIdsKey = collaborationIds.filter((id) => Number.isInteger(id)).join(',');

  useEffect(() => {
    if (!open) return;
    setTab('mine');
    setKeyword('');
    setSquareRecords([]);
    setSubmitting(false);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setExistingCreators([]);
      setSelectedIds([]);
      setExistingLoading(false);
      return;
    }
    const inviteIds = Array.from(new Set(collaborationIds.filter((id) => Number.isInteger(id))));
    if (!currentShopId || inviteIds.length === 0) {
      setExistingCreators([]);
      setSelectedIds([]);
      setExistingLoading(false);
      return;
    }
    let cancelled = false;
    setExistingLoading(true);
    Promise.all(inviteIds.map((id) => getCollaborationDetail(id, { shopId: currentShopId })))
      .then((results) => {
        if (cancelled) return;
        const failed = results.find((res) => res.code !== 200);
        if (failed) {
          if (failed.code !== -1) message.error(failed.message || t('invitation.detail_load_failed'));
          setExistingCreators([]);
          setSelectedIds([]);
          return;
        }
        const byId = new Map<string, TargetCollaborationCreator>();
        results.forEach((res) => {
          const list = res.content?.creators;
          if (!Array.isArray(list)) return;
          list.forEach((creator) => {
            const id = creator.creatorOpenId?.trim();
            if (!id || byId.has(id)) return;
            byId.set(id, creator);
          });
        });
        const next = Array.from(byId.values());
        const nextIds = next.map((creator) => creator.creatorOpenId?.trim() || '').filter(Boolean);
        setExistingCreators(next);
        setSelectedIds(nextIds);
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('invitation.detail_load_failed'));
          setExistingCreators([]);
          setSelectedIds([]);
        }
      })
      .finally(() => {
        if (!cancelled) setExistingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentShopId, inviteIdsKey, t]);

  const mineKeyword = tab === 'mine' ? keyword.trim() : '';
  const squareKeyword = tab === 'square' ? keyword.trim() : '';

  useEffect(() => {
    if (!open) return;
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setMineRecords([]);
      setMineTotal(0);
      setMineLoading(false);
      return;
    }
    let cancelled = false;
    setMineLoading(true);
    const timer = window.setTimeout(() => {
      const loadAll = async () => {
        const pageSize = 50;
        const all: InfluencerCollectionRecord[] = [];
        let pageNum = 1;
        let total = 0;
        while (pageNum <= 40) {
          const res = await getInfluencerCollectionPage({
            userCode,
            pageNum,
            pageSize,
            ...(mineKeyword ? { keyword: mineKeyword } : {})
          });
          if (cancelled) return;
          if (res.code !== 200) {
            if (res.code !== -1) message.error(res.message || t('creator.load_failed'));
            setMineRecords([]);
            setMineTotal(0);
            return;
          }
          const page = res.content;
          const records = page?.records;
          const list = Array.isArray(records) ? records : [];
          total = typeof page?.total === 'number' ? page.total : all.length + list.length;
          all.push(...list);
          if (list.length === 0 || all.length >= total) break;
          pageNum += 1;
        }
        if (cancelled) return;
        setMineRecords(all);
        setMineTotal(total);
      };
      loadAll()
        .catch(() => {
          if (!cancelled) {
            message.error(t('creator.load_failed'));
            setMineRecords([]);
            setMineTotal(0);
          }
        })
        .finally(() => {
          if (!cancelled) setMineLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, account?.userCode, mineKeyword, t]);

  useEffect(() => {
    if (!open || tab !== 'square') return;
    if (!squareKeyword) {
      setSquareRecords([]);
      setSquareLoading(false);
      return;
    }
    let cancelled = false;
    setSquareLoading(true);
    const timer = window.setTimeout(() => {
      getInfluencerPage({
        pageNum: 1,
        pageSize: 50,
        keyword: squareKeyword
      })
        .then((res) => {
          if (cancelled) return;
          if (res.code !== 200) {
            if (res.code !== -1) message.error(res.message || t('talent.load_failed'));
            setSquareRecords([]);
            return;
          }
          const list = res.content?.records;
          setSquareRecords(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) message.error(t('talent.load_failed'));
        })
        .finally(() => {
          if (!cancelled) setSquareLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, tab, squareKeyword, t]);

  const hasEmailLabel = t('invitation.has_email');
  const existingTalents = useMemo(
    () =>
      existingCreators
        .map((creator) => toPickerTalent(creatorToSource(creator), hasEmailLabel))
        .filter((item): item is PickerTalent => item != null),
    [existingCreators, hasEmailLabel]
  );
  const mineTalents = useMemo(
    () => mineRecords.map((row) => toPickerTalent(row, hasEmailLabel)).filter((item): item is PickerTalent => item != null),
    [mineRecords, hasEmailLabel]
  );
  const squareTalents = useMemo(
    () =>
      squareRecords.map((row) => toPickerTalent(row, hasEmailLabel)).filter((item): item is PickerTalent => item != null),
    [squareRecords, hasEmailLabel]
  );
  const visibleTalents = useMemo(() => {
    if (tab === 'mine') {
      const mineIds = new Set(mineTalents.map((item) => item.id));
      return [...existingTalents.filter((item) => !mineIds.has(item.id)), ...mineTalents];
    }
    return squareTalents;
  }, [tab, existingTalents, mineTalents, squareTalents]);
  const loading =
    existingLoading || (tab === 'mine' && mineLoading) || (tab === 'square' && squareLoading);

  const existingIdSet = useMemo(
    () =>
      new Set(existingCreators.map((creator) => creator.influencerId?.trim() || '').filter(Boolean)),
    [existingCreators]
  );
  const newlySelectedCount = selectedIds.filter((id) => !existingIdSet.has(id)).length;

  const toggleTalent = (id: string, checked: boolean) => {
    if (existingIdSet.has(id)) return;
    setSelectedIds((prev) => (checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((item) => item !== id)));
  };

  const submit = async () => {
    if (submitting || existingLoading) return;
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    const inviteIds = Array.from(new Set(collaborationIds.filter((id) => Number.isInteger(id))));
    if (inviteIds.length === 0) {
      message.warning(t('invitation.invite_pick_required'));
      return;
    }
    if (inviteIds.length > 10) {
      message.warning(t('invitation.invite_limit'));
      return;
    }
    const influencerIds = Array.from(
      new Set(selectedIds.map((id) => id.trim()).filter((id) => id && !existingIdSet.has(id)))
    );
    if (influencerIds.length === 0) {
      message.warning(t('invitation.pick_talent'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await addCollaborationCreators({
        shopId: currentShopId,
        collaborationIds: inviteIds,
        influencerIds
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('invitation.invite_failed'));
        return;
      }
      message.success(res.message || t('invitation.invite_success'));
      onSuccess?.();
      onClose();
    } catch {
      message.error(t('invitation.invite_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const tabBtnClass = (active: boolean) =>
    active
      ? 'rounded-full border-0 bg-[#596ae8]/10 px-3 py-1 text-xs font-medium text-[#596ae8]'
      : 'cursor-pointer rounded-full border border-[#eef0f4] bg-white px-3 py-1 text-xs text-slate-600 hover:border-[#596ae8]/30';

  return (
    <Modal
      title={t('invitation.add_talent_title')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={560}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      closeIcon={
        <span className='cursor-pointer text-sm font-normal text-slate-500 hover:text-slate-700'>{t('common.close')}</span>
      }
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type='primary' className='wb-btn-primary rounded-lg' loading={submitting} onClick={submit}>
            {t('invitation.add')}
          </Button>
        </div>
      }>
      <div className='space-y-3'>
        <p className='mb-0 text-xs leading-relaxed text-slate-400'>{t('invitation.batch_invite_hint')}</p>
        <p className='mb-0 text-xs leading-relaxed text-slate-400'>
          {newlySelectedCount === 0
            ? t('invitation.none_selected')
            : t('invitation.selected_count', { count: newlySelectedCount })}
        </p>
        <Input
          allowClear
          placeholder={t('invitation.search_placeholder')}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <div className='flex flex-wrap items-center gap-2'>
          <button type='button' className={tabBtnClass(tab === 'mine')} onClick={() => setTab('mine')}>
            {t('invitation.my_talents', { count: mineTalents.length })}
          </button>
          <button type='button' className={tabBtnClass(tab === 'square')} onClick={() => setTab('square')}>
            {t('invitation.search_square')}
          </button>
        </div>
        <div className='max-h-[240px] overflow-y-auto rounded-lg border border-[#eef0f4] bg-[#fafbfc] px-2 py-1'>
          {loading ? (
            <div className='flex justify-center py-6'>
              <Spin size='small' />
            </div>
          ) : null}
          {!loading
            ? visibleTalents.map((talent) => {
                const alreadyInInvite = existingIdSet.has(talent.id);
                return (
                  <label
                    key={talent.id}
                    className={`flex items-center gap-3 rounded-md px-2 py-2.5 ${
                      alreadyInInvite ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-white'
                    }`}>
                    <Checkbox
                      checked={selectedIds.includes(talent.id) || alreadyInInvite}
                      disabled={alreadyInInvite}
                      onChange={(e) => toggleTalent(talent.id, e.target.checked)}
                    />
                    {talent.avatarUrl ? (
                      <img src={talent.avatarUrl} alt='' className='size-8 shrink-0 rounded-full object-cover' />
                    ) : (
                      <span className='inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-[#596ae8] text-xs font-semibold text-white'>
                        {talent.initial}
                      </span>
                    )}
                    <span className='min-w-0 flex-1'>
                      <span className='block text-sm font-semibold text-slate-800'>{talent.name}</span>
                      <span className='block truncate text-xs text-slate-500'>{talent.meta}</span>
                    </span>
                  </label>
                );
              })
            : null}
          {!loading && visibleTalents.length === 0 ? (
            <p className='px-2 py-8 text-center text-sm text-slate-400'>
              {tab === 'square' && !squareKeyword ? t('invitation.square_prompt') : t('invitation.no_match')}
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
